// Shared widget chrome (TASK-493, ports SixEase WidgetContainer, re-tokenised to
// Kielis shadcn tokens). Titled panel + a "source" provenance-badge slot that
// ported widgets (TASK-496) fill so every figure traces to its origin.
// TASK-651: every data widget also shows WHEN it loaded (freshness), flags
// staleness, explains itself on demand ("Why this?"), and lets the RM report a
// data issue from the widget's own context.
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { HelpCircle, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { taskService } from '../../services/taskService';
import type { TaskCreateRequest } from '../../types/tasks';
import { getDerivation, type DerivationStep } from '../../services/knowledgeGraphService';
import { useWidgetProvenance } from '../../registry/widgetProvenance';
import { ProvenanceChain, type ProvenanceStep } from '../provenance/ProvenanceChain';

interface WidgetContainerProps {
  // Optional: widgets that already render their own title pass `source` only, so
  // the header shows just the provenance badge (no double-title). TASK-494.
  title?: string;
  source?: string;
  children: ReactNode;
  className?: string;
  badges?: ReactNode;
  // Optional strip below the header, above the padded scroll body — the home for
  // on-tile filter chrome (WidgetFilterBar, TASK-495). Kept outside the body padding
  // so it reads as part of the frame, not the content.
  toolbar?: ReactNode;
  /**
   * TASK-825: when this widget's DATA was true. Not when the tile mounted.
   *
   * The stamp read `useState(() => Date.now())` and rendered it as **"as of"**, and staleness
   * was `Date.now() - loadedAt > STALE_AFTER_MS`. Both numbers were about the WIDGET; there
   * was no data timestamp input at all. Verified live 2026-08-25: Morning Book Digest showed
   * "as of 07:03 AM" against a browser clock of 07:03:48 while its content was dated
   * 2026-08-07 — **18 days old** — and re-rendering moved the stamp to "now" every time.
   *
   * So the badge was structurally incapable of reporting the one thing it claimed to: a tile
   * was "fresh" for 30 minutes after mounting however old its content was, then turned
   * "stale" purely from sitting on screen while its content did not change.
   *
   * **Absent means UNKNOWN, never `Date.now()`.** Defaulting it to the clock would preserve
   * every current caller and re-create the exact defect under a new name — so a widget that
   * cannot say when its data was true says "loaded HH:MM" and does not say "as of".
   * Where a payload carries no timestamp, that is a finding for that endpoint, not a licence
   * to fall back to the clock.
   */
  dataAsOf?: string | number | Date | null;
}

const STALE_AFTER_MS = 30 * 60_000;

/**
 * TASK-683: turn a stored claim into a chain step, and into a link that opens the source
 * where the sentence actually is. Documents open in the KYC workspace's viewer at the cited
 * page; a claim whose source we cannot address gets no link rather than a dead one.
 */
function toChainStep(step: DerivationStep, clientId?: string): ProvenanceStep {
  const page = step.locator?.page;
  const href =
    clientId && step.source_document_id
      ? `/clients/${clientId}/kyc-workspace?doc=${step.source_document_id}` +
        (typeof page === 'number' ? `&page=${page}` : '')
      : null;

  return {
    excerpt: step.excerpt,
    locator: step.locator,
    fieldPath: step.field_path,
    value: step.value,
    observationCount: step.observation_count,
    origin: step.origin,
    sourceLabel: step.source_document_name ?? step.origin,
    href,
    // TASK-683 review: meeting claims carry no document, so `href` is null for every cited
    // claim in the graph today. The resolved session + utterance open the transcript instead.
    sessionId: step.source_session_id,
    utteranceSeq: step.source_utterance_seq,
    verified: step.verified,
    confidence: step.confidence,
  };
}

export function WidgetContainer({
  title, source, children, className, badges, toolbar, dataAsOf,
}: WidgetContainerProps) {
  const [loadedAt] = useState(() => Date.now());
  const [stale, setStale] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [reporting, setReporting] = useState(false);

  // TASK-683: the derivation behind this widget's figures. Fetched when the RM asks "why",
  // never on mount — a canvas of 12 tiles must not fire 12 requests nobody asked for.
  const provenance = useWidgetProvenance();
  const [chain, setChain] = useState<DerivationStep[] | null>(null);
  const [loadingChain, setLoadingChain] = useState(false);

  const openWhy = useCallback(async () => {
    const next = !whyOpen;
    setWhyOpen(next);
    if (!next || chain !== null || !provenance?.clientId) return;
    setLoadingChain(true);
    try {
      const response = await getDerivation(provenance.clientId, provenance.component);
      setChain(response?.steps ?? []);
    } catch {
      // A read-only explanation must never interrupt the RM. No chain = the honest
      // "no derivation recorded" state, same as a widget that has none.
      setChain([]);
    } finally {
      setLoadingChain(false);
    }
  }, [whyOpen, chain, provenance]);

  // TASK-825: the data's own timestamp, or null when the widget cannot say. Parsed once —
  // an unparseable value is `null` (unknown), never "now": a stamp we cannot trust must read
  // as absent rather than as fresh.
  const dataAt = useMemo(() => {
    if (dataAsOf === undefined || dataAsOf === null || dataAsOf === '') return null;
    const ms = dataAsOf instanceof Date ? dataAsOf.getTime() : new Date(dataAsOf).getTime();
    return Number.isFinite(ms) ? ms : null;
  }, [dataAsOf]);

  useEffect(() => {
    // Measured against the DATA where there is one, and evaluated IMMEDIATELY rather than
    // only on the next tick — an 18-day-old payload must read stale on its first frame, not
    // 60 seconds later.
    const measure = () =>
      setStale(Date.now() - (dataAt ?? loadedAt) > STALE_AFTER_MS);
    measure();
    const timer = window.setInterval(measure, 60_000);
    return () => window.clearInterval(timer);
  }, [loadedAt, dataAt]);

  const clockLabel = (ms: number) => {
    const d = new Date(ms);
    const sameDay = new Date().toDateString() === d.toDateString();
    // Older than today, the time of day is not the useful part — the DATE is. "as of 07:03"
    // on 18-day-old content is exactly how the defect read as fresh.
    return sameDay
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const loadedLabel = clockLabel(loadedAt);
  const dataLabel = dataAt === null ? null : clockLabel(dataAt);

  // TASK-651: report/correct a generated fact from its context — creates a real
  // follow-up task so the report has an owner and a lifecycle.
  const reportIssue = async () => {
    setReporting(true);
    try {
      await taskService.createTask({
        title: `Data correction: ${title || source || 'generated view'}`,
        description:
          `Reported from the ${title || 'generated'} widget (source: ${source || 'unknown'}, ` +
          `loaded ${loadedLabel}). Review the displayed facts and correct the underlying record.`,
        task_type: 'follow_up_other',
        priority: 'medium',
        source: 'data_correction',
      } as TaskCreateRequest);
      toast.success('Reported — a correction task was created in your Do list');
      setWhyOpen(false);
    } catch {
      toast.error('Could not create the correction task — try again');
    } finally {
      setReporting(false);
    }
  };

  return (
    <div
      data-testid="widget-container"
      // Widgets are addressable by what the RM sees on them: a title, or the source badge
      // for the ones that render their own heading. Without this a test has to guess at
      // DOM shape to find a tile's chrome.
      data-widget-label={title || source || 'view'}
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm',
        className,
      )}
    >
      {/* TASK-683: inside a canvas tile, CanvasTile's hover chrome (collapse/maximize/pin/
          close) is absolutely positioned over the top-right corner — exactly where this
          header's controls sit. It swallowed the click on "Why this?", making the
          explanation unreachable on the canvas. Reserve the corner for it; outside a tile
          (KYC panels, embedded uses) there is no overlay and no reserve. */}
      <div
        className={cn(
          'flex items-center justify-between border-b border-border bg-muted px-4 py-3',
          provenance && 'pr-28',
        )}
      >
        <div className="flex items-center gap-2">
          {title && (
            <h3 className="text-[13px] font-semibold tracking-wide text-foreground">{title}</h3>
          )}
          {badges}
        </div>
        <div className="relative flex items-center gap-1.5">
          {stale ? (
            <span
              data-testid="widget-stale"
              className="rounded-full bg-status-attention/10 px-2 py-0.5 text-[10px] font-medium text-status-attention-fg"
            >
              stale · {dataLabel ? `as of ${dataLabel}` : `loaded ${loadedLabel}`}
            </span>
          ) : dataLabel ? (
            <span data-testid="widget-freshness" className="text-[10px] text-muted-foreground">
              as of {dataLabel}
            </span>
          ) : (
            // TASK-825: no data timestamp — say what IS known. "as of" names the data's
            // currency and this is the render clock, so claiming it would be an affirmative
            // false statement rather than a missing feature.
            <span data-testid="widget-loaded-only" className="text-[10px] text-muted-foreground">
              loaded {loadedLabel}
            </span>
          )}
          {source && (
            <div className="flex items-center gap-1.5 rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              <ShieldAlert className="h-3 w-3" />
              <span>{source}</span>
            </div>
          )}
          {/* TASK-683 review: this used to be a bare 18px HelpCircle with no visible text,
              sitting in a row with CanvasTile's collapse/maximize/pin/CLOSE glyphs — five
              near-identical grey icons, and the RM never found the one that explains the
              product. The epic's whole claim ("every figure traces to the sentence it came
              from") is behind this control, so it says what it is. */}
          <button
            type="button"
            onClick={openWhy}
            aria-expanded={whyOpen}
            aria-label="Why this view?"
            title="Why this view?"
            data-testid="widget-why-button"
            className={cn(
              'flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors',
              whyOpen
                ? 'border-foreground/30 bg-accent text-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            )}
          >
            <HelpCircle className="h-3 w-3" />
            <span>Why this?</span>
          </button>
          {whyOpen && (
            <div
              data-testid="widget-why-popover"
              className="absolute right-0 top-6 z-20 max-h-96 w-80 overflow-y-auto rounded-lg border border-border bg-popover p-3 text-left shadow-md"
            >
              <p className="text-xs font-semibold text-foreground">
                {title || 'Generated view'}
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Loaded live at {loadedLabel}
                {source ? ` from ${source}` : ''}. Nothing here is authored by the model.
              </p>

              {/* TASK-683: the actual derivation — source sentence, where it sits, how well
                  corroborated — replacing the blanket disclaimer that named no figure. */}
              {loadingChain ? (
                <p
                  data-testid="widget-why-loading"
                  className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Tracing the figures…
                </p>
              ) : chain && chain.length ? (
                <>
                  <p className="mt-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Derived from
                  </p>
                  <ProvenanceChain
                    className="mt-1.5"
                    steps={chain.map((step) => toChainStep(step, provenance?.clientId))}
                    derivedLabel={`shown in ${title || 'this view'}`}
                  />
                </>
              ) : (
                <p data-testid="widget-why-empty" className="mt-2 text-xs text-muted-foreground">
                  No derivation recorded for this view — its figures come from live records
                  rather than extracted claims, so there is no source sentence to cite.
                </p>
              )}
              <button
                type="button"
                onClick={reportIssue}
                disabled={reporting}
                className="mt-2 rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
              >
                {reporting ? 'Reporting…' : 'Report a data issue'}
              </button>
            </div>
          )}
        </div>
      </div>
      {toolbar}
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  );
}
